/**
 * Toast — Lightweight ephemeral feedback.
 * Usage:
 *   const { toast } = useToast();
 *   toast('Saved');
 *   toast('Failed to save', { variant: 'error' });
 *
 * Wrap app in <ToastProvider> to enable.
 */

import { createContext, useContext, useState, useCallback } from 'react';
import { Check, AlertCircle, Info } from 'lucide-react';

const ToastContext = createContext({ toast: () => {} });

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((message, opts = {}) => {
    const id = Date.now() + Math.random();
    const variant = opts.variant || 'success';
    const duration = opts.duration ?? 2400;
    setToasts(prev => [...prev, { id, message, variant }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        className="fixed z-50 pointer-events-none flex flex-col gap-2 items-center sm:items-end
                   bottom-20 sm:bottom-auto sm:top-4 left-2 right-2 sm:left-auto sm:right-4 pb-safe"
        role="region"
        aria-live="polite"
        aria-label="Notifications"
      >
        {toasts.map(t => (
          <ToastItem key={t.id} {...t} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ message, variant }) {
  const styles = {
    success: 'bg-green-900/90 border-green-700/50 text-green-100',
    error: 'bg-red-900/90 border-red-700/50 text-red-100',
    info: 'bg-slate-800/90 border-slate-700/50 text-slate-100',
  };
  const Icon = variant === 'success' ? Check : variant === 'error' ? AlertCircle : Info;

  return (
    <div
      className={`pointer-events-auto flex items-center gap-2 px-4 py-3 rounded-lg border shadow-xl
                  text-sm font-medium animate-fade-in-up backdrop-blur-sm max-w-sm ${styles[variant]}`}
      role="status"
    >
      <Icon size={16} className="flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
